"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";
import { cn, renderRichText } from "@/lib/utils";
import { COLOR_SCHEMES } from "@/lib/colorSchemes";
import { Button } from "@/components/ui/button";
import type { BannerProps } from "./types";
import {
  STANDARD_BANNER_LAYOUT,
  BANNER_CONTENT_ALIGN_CLASSES,
  HEADING_SIZE_CLASS,
  Z,
  safeColor,
} from "./types";

// -----------------------------------------------------------------------------
// StandardBanner — 2-column text + image layout.
//
// Differences from HeroBanner:
// - No rotating-word support (static accent word only).
// - Image column uses a standard responsive <Image> with object-cover cropping.
// - Default headingSize is "2xl" instead of "banner".
// - BannerShell handles full-bleed backgroundImage on the container.
// -----------------------------------------------------------------------------

export function StandardBanner({
  eyebrow,
  title = "",
  rotatingWords = [],
  description,
  cta,
  colorScheme = "default",
  image,
  accentWordColor,
  headingSize = "2xl",
  layout = "7/5",
  reversed = false,
  contentAlign = "center-left",
}: BannerProps) {
  const scheme = COLOR_SCHEMES[colorScheme];
  const lc = STANDARD_BANNER_LAYOUT[layout] ?? STANDARD_BANNER_LAYOUT["7/5"];
  const alignCls =
    BANNER_CONTENT_ALIGN_CLASSES[contentAlign] ??
    BANNER_CONTENT_ALIGN_CLASSES["center-left"];

  // ── Title parsing (accent word, no rotation) ─────────────
  const hasPlaceholder = title.includes("{word}");
  const staticWord = hasPlaceholder ? (rotatingWords[0] ?? "") : "";
  const showAccentWord = hasPlaceholder && staticWord.length > 0;
  const firstPlaceholderIdx = title.indexOf("{word}");
  const before = hasPlaceholder ? title.slice(0, firstPlaceholderIdx) : title;
  const after = hasPlaceholder
    ? title.slice(firstPlaceholderIdx + "{word}".length)
    : "";

  const resolvedAccent = useMemo(
    () => safeColor(accentWordColor, "#19557f"),
    [accentWordColor],
  );

  return (
    <div className="banner max-w-content mx-auto w-full h-full">
      <div className="wrapper flex flex-col md:flex-row w-full h-full md:gap-8">
        <div
          className={cn(
            "content-wrapper flex flex-col justify-center gap-8 md:gap-4 max-[400px]:pb-18 pt-18 md:pt-0",
            contentAlign?.startsWith("center") ? "items-center" : "items-start",
            contentAlign?.endsWith("left")
              ? "lg:items-start"
              : "lg:items-center",
            reversed ? lc.image : lc.text,
            reversed && "md:order-last",
            alignCls,
          )}
          style={{ zIndex: Z.text }}
        >
          {eyebrow && (
            <p className={cn("body-md font-semibold", scheme.bodyMuted)}>
              {eyebrow}
            </p>
          )}
          <h1
            className={cn(
              HEADING_SIZE_CLASS[headingSize],
              "font-semibold",
              scheme.heading,
            )}
          >
            {before}
            {showAccentWord && (
              <span style={{ color: resolvedAccent }}>{staticWord}</span>
            )}
            {after}
          </h1>
          <div className={cn("flex lg:flex-col ", scheme.body)}>
            <div className="flex flex-col max-[400px]:basis-full basis-1/2 md:basis-full items-start gap-8 md:gap-4">
              {description && (
                <p className={cn("body-md", scheme.bodyMuted)}>
                  {renderRichText(description)}
                </p>
              )}
              {cta && (
                <Button
                  variant="default"
                  size="default"
                  nativeButton={false}
                  render={
                    cta.href.startsWith("#") ? (
                      <a href={cta.href} />
                    ) : (
                      <Link href={cta.href} />
                    )
                  }
                >
                  {cta.label}
                </Button>
              )}
            </div>
            {image?.mobileSrc && (
              <div className="basis-1/2 overflow-hidden max-[400px]:hidden md:hidden">
                <Image
                  src={image.mobileSrc}
                  alt={image.mobileAlt ?? image.alt}
                  width={640}
                  height={480}
                  priority
                  className="w-full h-auto object-contain"
                  sizes="50vw"
                />
              </div>
            )}
          </div>
        </div>
        {image && (
          <div
            className={cn(
              "image-wrapper overflow-hidden max-h-145",
              reversed ? lc.text : lc.image,
              reversed && "md:order-first",
            )}
            style={{ zIndex: Z.image }}
          >
            <Image
              src={image.src}
              alt={image.alt}
              width={640}
              height={480}
              priority
              className="w-full h-full object-contain pt-8 hidden md:block"
              sizes="(min-width: 1024px) 42vw, 100vw"
            />
          </div>
        )}
      </div>
    </div>
  );
}
